import React, { useCallback, useState } from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import { IPlanInfo20Fields } from '../../../types/contentful';

import CollapsibleContainer from '../Collapsible/CollapsibleContainer';
import { Flexbox } from '../Flexbox';
import { Text } from '../Text';
import RichText from '../RichText';

import {
  Container,
  PlanInfoWrapper,
  PlanInfoHeader,
  PlanTitle,
  PlanText,
  PriceText,
  Badge,
  BadgeIcon,
  BadgeText,
  PlanDetails,
  DescriptionWrapper,
  SeeMoreWrapper,
  ToggleIcon,
  Details,
  PlanList,
  PlanListItem
} from './styles';

import yellowPlanBadge from '@/images/yellow-plan-badge.svg';
import plus from '@/images/plus.svg';

export function CollapsiblePlanInfo({
  planName,
  price,
  color,
  badge,
  description,
  expandField,
  collapseField,
  enabledFields,
  disabledFields,
  button,
  useLightText,
  boxShadowTopGradient,
  boxShadowBottomGradient
}: IPlanInfo20Fields) {
  const [isOpen, setIsOpen] = useState(false);

  const RICH_TEXT_OVERRIDES: RenderNode = {
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <PlanListItem node={node}>{children}</PlanListItem>
    )
  };

  const toggleSeeMore = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  return (
    <Container role="group" aria-label={planName}>
      <PlanInfoWrapper
        expand={isOpen}
        topGradient={boxShadowTopGradient as string}
        bottomGradient={boxShadowBottomGradient as string}
      >
        <PlanInfoHeader color={color || ''}>
          {!!badge && (
            <Badge>
              <BadgeIcon src={yellowPlanBadge} alt="Best value plan" />
              <BadgeText
                fontSize={{ 0: '2xs', desktop: 'sm' }}
                fontWeight="700"
              >
                {badge}
              </BadgeText>
            </Badge>
          )}
          <PlanTitle lightText={!!useLightText}>
            <PlanText as="h3">{planName}</PlanText>
            <PriceText as="p">{price}</PriceText>
          </PlanTitle>
        </PlanInfoHeader>

        <PlanDetails>
          {description && (
            <DescriptionWrapper>
              <RichText data={description} />
            </DescriptionWrapper>
          )}

          {isOpen && (
            <CollapsibleContainer>
              <Details
                flexDirection="column"
                flex={1}
                marginTop="ml"
                marginBottom={button ? 'ml' : 0}
              >
                <RichText
                  data={enabledFields}
                  overrideOptions={{
                    [BLOCKS.UL_LIST]: (_node, children) => (
                      <PlanList useCheckMark={true}>{children}</PlanList>
                    ),
                    ...RICH_TEXT_OVERRIDES
                  }}
                />
                <RichText
                  colour="collapsibleBorder"
                  data={disabledFields}
                  overrideOptions={{
                    [BLOCKS.UL_LIST]: (_node, children) => (
                      <PlanList useCheckMark={false}>{children}</PlanList>
                    ),
                    ...RICH_TEXT_OVERRIDES
                  }}
                />
              </Details>
              <Flexbox>
                <RichText data={button} />
              </Flexbox>
            </CollapsibleContainer>
          )}
        </PlanDetails>

        <SeeMoreWrapper
          flexDirection="row"
          alignItems="center"
          onClick={toggleSeeMore}
          data-testid="btn-see-more"
        >
          <ToggleIcon
            src={plus}
            alt={isOpen ? 'Close section' : 'Open section'}
            isOpen={isOpen}
          />
          <Text
            as="p"
            fontSize={{ 0: 'sm', tablet: 'm' }}
            fontWeight="500"
            lineHeight="ml"
            margin={{ 0: [0, 'auto'] }}
            data-testid="btn-expand"
          >
            {isOpen ? collapseField : expandField}
          </Text>
        </SeeMoreWrapper>
      </PlanInfoWrapper>
    </Container>
  );
}
